import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Check,
  PhoneCall,
  MessageCircle,
  UserCheck,
  ClipboardList,
  Radio,
  DatabaseBackup,
  MapPin,
  ShieldCheck,
} from "lucide-react";

const FEATURES = [
  { icon: UserCheck, title: "Attendance & schedules", body: "Per-employee check-in/out times, late detection, overtime tracking, exportable timesheets." },
  { icon: MessageCircle, title: "Team Chat + WhatsApp inbox", body: "Internal group chat and a real WhatsApp Business inbox side-by-side. Text, photos, videos." },
  { icon: ClipboardList, title: "Tasks & customers", body: "Assign tasks, track outcomes, keep a shared customer log with per-employee visibility." },
  { icon: PhoneCall, title: "Call logs & recordings", body: "Every business call captured with duration, outcome and (optional) recording." },
  { icon: Radio, title: "IVR builder", body: "Design your greet-and-route flow visually — no code, no phone-system consultants." },
  { icon: MapPin, title: "Live employee map", body: "See where field employees are and their check-in status in one glance." },
  { icon: DatabaseBackup, title: "Backup & restore", body: "One-click DB + media archive. Restore in minutes if anything goes wrong." },
  { icon: ShieldCheck, title: "Multi-admin, isolated", body: "Add co-admins for a bigger company — each with fully separate data by default." },
];

export default function Landing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [trialDays, setTrialDays] = useState(30);

  useEffect(() => {
    if (user) nav(user.role === "owner" ? "/dashboard" : "/app", { replace: true });
  }, [user, nav]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/billing/plans");
        setPlans(data.plans || []);
        setTrialDays(data.trial_days || 30);
      } catch { /* ignore */ }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="landing-page">
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-5 h-14 flex items-center justify-between">
          <div className="font-heading font-bold text-lg flex items-center gap-2">
            <span className="inline-block w-2 h-2 bg-[#0055FF] rounded-full" />
            DialFlow
          </div>
          <nav className="flex items-center gap-4 text-sm">
            <a href="#features" className="text-slate-600 hover:text-slate-900 hidden sm:inline">Features</a>
            <a href="#pricing" className="text-slate-600 hover:text-slate-900">Pricing</a>
            <Link to="/login" className="text-slate-600 hover:text-slate-900" data-testid="landing-signin">Sign in</Link>
            <Button onClick={() => nav("/signup")} className="bg-[#0055FF] hover:bg-[#0040CC] h-8" data-testid="landing-start">
              Start free <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </nav>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-5 pt-16 pb-14 text-center">
        <div className="mono text-[10px] uppercase tracking-widest text-[#0055FF] mb-4">
          For growing service, sales and field teams
        </div>
        <h1 className="text-4xl sm:text-5xl md:text-6xl font-heading font-bold leading-tight tracking-tight">
          Run your team.<br/>Not your tools.
        </h1>
        <p className="max-w-2xl mx-auto mt-5 text-slate-600 text-lg">
          Attendance, tasks, customer chat and WhatsApp — one clean workspace
          your owner and employees actually enjoy using. Free for {trialDays} days,
          no card required.
        </p>
        <div className="flex items-center justify-center gap-3 mt-8">
          <Button onClick={() => nav("/signup")} className="bg-[#0055FF] hover:bg-[#0040CC] h-11 px-6" data-testid="landing-hero-start">
            Start free trial <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
          <a href="#pricing" className="text-slate-600 hover:text-slate-900 text-sm underline">See pricing</a>
        </div>
        <div className="mt-4 mono text-[10px] uppercase tracking-widest text-slate-500">
          {trialDays}-day free trial · No credit card · Cancel anytime
        </div>
      </section>

      <section id="features" className="max-w-6xl mx-auto px-5 py-14 border-t border-slate-100">
        <div className="max-w-2xl">
          <h2 className="text-3xl font-heading font-bold">Everything your ops team needs</h2>
          <p className="text-slate-600 mt-2">DialFlow bundles the tools you were duct-taping together into a single opinionated workspace.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
          {FEATURES.map((f) => (
            <div key={f.title} className="p-4 rounded-lg border border-slate-100 bg-slate-50/40">
              <f.icon className="w-5 h-5 text-[#0055FF] mb-2" />
              <div className="font-heading font-semibold text-[15px]">{f.title}</div>
              <div className="text-sm text-slate-600 mt-1">{f.body}</div>
            </div>
          ))}
        </div>
      </section>

      <section id="pricing" className="max-w-6xl mx-auto px-5 py-14 border-t border-slate-100">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-3xl font-heading font-bold">Simple, honest pricing</h2>
          <p className="text-slate-600 mt-2">
            {trialDays}-day free trial on every plan. Prices are in INR, billed monthly. Cancel any time.
          </p>
        </div>
        <div className="grid md:grid-cols-4 gap-4 mt-10" data-testid="landing-pricing">
          {plans.map((p) => {
            const isEnterprise = p.key === "enterprise";
            const highlight = p.key === "growth";
            return (
              <div key={p.key} className={"rounded-xl border p-5 flex flex-col " + (highlight ? "border-[#0055FF] shadow-md" : "border-slate-200")} data-testid={`landing-plan-${p.key}`}>
                <div className="flex items-center justify-between">
                  <div className="font-heading font-bold text-lg">{p.name}</div>
                  {highlight && <span className="mono text-[9px] uppercase text-[#0055FF] bg-blue-50 border border-blue-100 rounded px-2 py-0.5">popular</span>}
                </div>
                <div className="mt-3">
                  {isEnterprise ? <div className="text-2xl font-heading font-bold">Custom</div> :
                    <><span className="text-3xl font-heading font-bold">₹{p.price_inr.toLocaleString("en-IN")}</span><span className="text-slate-500 text-sm"> /mo</span></>}
                </div>
                <ul className="mt-4 space-y-1.5 flex-1">
                  {p.features.map((ft) => (
                    <li key={ft} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />{ft}
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => isEnterprise ? (window.location.href = "mailto:ccs.bud@gmail.com?subject=DialFlow Enterprise") : nav(`/signup?plan=${p.key}`)}
                  className={"mt-5 w-full " + (highlight ? "bg-[#0055FF] hover:bg-[#0040CC]" : "bg-slate-900 hover:bg-slate-800")}
                  data-testid={`landing-plan-cta-${p.key}`}
                >
                  {isEnterprise ? "Contact sales" : "Start free trial"}
                </Button>
              </div>
            );
          })}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 py-16 border-t border-slate-100 text-center">
        <h3 className="text-2xl font-heading font-bold">Ready to run your team better?</h3>
        <p className="text-slate-600 mt-2">{trialDays} days on us. No card, no meetings, no pushy sales.</p>
        <Button onClick={() => nav("/signup")} className="bg-[#0055FF] hover:bg-[#0040CC] h-11 px-6 mt-5" data-testid="landing-footer-start">
          Start free trial <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </section>

      <footer className="border-t border-slate-100 py-8 text-center text-xs text-slate-500 mono">
        © {new Date().getFullYear()} DialFlow · made in India
      </footer>
    </div>
  );
}
